/**
 * The page: filter row, overview, what needs attention, then the targets.
 *
 * The filters live here and scope everything below them, and they are applied at two
 * depths on purpose. The **site chips** narrow the estate: everything, overview
 * included, is computed from `scoped`, so the headline never describes hosts you have
 * filtered away. The **health slice** narrows only what you are inspecting right now -
 * the grid, the table and the attention panel run on `visible`, while the overview stays
 * on `scoped` and merely re-points its hero figure at whatever the slice is asking about
 * (see Overview.heroFigure). An overview that shrank to the health slice could only ever
 * say "1 of 1", which is true of every filter and therefore says nothing.
 *
 * Both roll-ups are recomputed locally rather than taken from the server's, which covers
 * every target - showing that above a filtered grid would put a headline on screen that
 * the cards below contradict.
 *
 * During a refetch the previous render is held at reduced opacity rather than replaced
 * by a skeleton: no layout jump, and nothing flashes on a wall display.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import type {
  ApiKeyCheck,
  ManualTargetRequest,
  NewCategoryRequest,
  Rollup,
  TargetResult,
  TargetSettings,
} from './api/types'
import { AddCategoryForm } from './components/AddCategoryForm'
import { AttentionPanel } from './components/AttentionPanel'
import { EditManualForm } from './components/EditManualForm'
import { FilterBar, applyWindow, matchesChips, matchesHealth } from './components/FilterBar'
import type { FilterChip, Filters } from './components/FilterBar'
import { Header } from './components/Header'
import { ManualTargetForm } from './components/ManualTargetForm'
import { Overview } from './components/Overview'
import { SortableGrid } from './components/SortableGrid'
import { TableView } from './components/TableView'
import { TargetCard } from './components/TargetCard'
import { useAuth, useLogout } from './hooks/useAuth'
import { useCardOrder } from './hooks/useCardOrder'
import {
  useAddCategory,
  useCheckApiKey,
  useDashboard,
  useDeleteTarget,
  useProbe,
  useSetGlobalInterval,
  useSetInterval,
  useSetMode,
  useUpdateManualTarget,
} from './hooks/useDashboard'
import { useHashRoute, targetHref } from './hooks/useHashRoute'
import { useTheme } from './hooks/useTheme'
import { LoginPage } from './LoginPage'
import { cn } from './lib/cn'
import { STATUS_ORDER, worstStatus } from './lib/status'
import { TargetDetailPage } from './TargetDetailPage'

const DEFAULT_FILTERS: Filters = {
  siteIds: [],
  health: 'all',
  window: 'all',
  view: 'cards',
}

export default function App() {
  const { route, navigate } = useHashRoute()
  const auth = useAuth()

  // Send the login route to the dashboard once authenticated, and vice-versa. Done in an
  // effect so navigation is a side effect of auth state, not something each render fights.
  useEffect(() => {
    if (auth.isLoading) return
    if (auth.gated && route.name !== 'login') navigate({ name: 'login' })
    else if (!auth.gated && route.name === 'login') navigate({ name: 'dashboard' })
  }, [auth.isLoading, auth.gated, route.name, navigate])

  // Nothing renders until we know whether a session is required - so the login form
  // never flashes in front of a valid session, nor the dashboard in front of the gate.
  if (auth.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-ink-secondary">
        Loading…
      </div>
    )
  }

  if (auth.gated || route.name === 'login') {
    return <LoginPage />
  }

  // A single component tree either way, so the shared snapshot query is not torn down
  // and refetched when moving between the grid and a detail page.
  if (route.name === 'target') {
    return (
      <TargetDetailPage
        targetId={route.targetId}
        onBack={() => navigate({ name: 'dashboard' })}
      />
    )
  }
  return <DashboardPage onOpenTarget={(id) => navigate({ name: 'target', targetId: id })} />
}

function DashboardPage({ onOpenTarget }: { onOpenTarget: (targetId: string) => void }) {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)
  const [autoRefresh, setAutoRefresh] = useState(true)
  //: The manual target whose inline edit panel is open, if any.
  const [editingId, setEditingId] = useState<string | null>(null)

  const theme = useTheme()
  const auth = useAuth()
  const logout = useLogout()
  const probe = useProbe()
  const deleteTarget = useDeleteTarget()
  const updateManual = useUpdateManualTarget()
  const setMode = useSetMode()
  const checkApiKey = useCheckApiKey()
  const setTargetInterval = useSetInterval()
  const setGlobalInterval = useSetGlobalInterval()
  const addCategory = useAddCategory()
  const {
    config,
    configError,
    snapshot,
    snapshotError,
    pollIntervalSeconds,
    thresholds,
    isLoading,
    isRefetching,
  } = useDashboard(autoRefresh)

  const results = snapshot?.results ?? []

  // Per-target settings the backend confirmed (mode + interval). Seeded lazily from the
  // settings each mutation returns, so a card shows its own interval without a query per
  // card; anything not here inherits the global interval.
  const [targetSettings, setTargetSettings] = useState<
    Record<string, { poll_interval_seconds: number; custom_interval: boolean }>
  >({})
  const rememberSettings = useCallback(
    (s: TargetSettings) =>
      setTargetSettings((prev) => ({
        ...prev,
        [s.target_id]: {
          poll_interval_seconds: s.poll_interval_seconds,
          custom_interval: s.custom_interval,
        },
      })),
    [],
  )

  // The last "Check key" verdict per target. Kept here rather than in each card so the
  // answer survives the card re-rendering on the next snapshot poll - a verdict that
  // vanished a few seconds after being asked for would be useless.
  const [keyChecks, setKeyChecks] = useState<Record<string, ApiKeyCheck>>({})
  const [keyCheckErrors, setKeyCheckErrors] = useState<Record<string, string>>({})
  const onCheckKey = useCallback(
    (targetId: string) => {
      setKeyCheckErrors(({ [targetId]: _dropped, ...rest }) => rest)
      checkApiKey
        .mutateAsync(targetId)
        .then((check) => setKeyChecks((prev) => ({ ...prev, [targetId]: check })))
        .catch((error: Error) => {
          // The check's own failure is distinct from a "key rejected" verdict, so it is
          // shown as such instead of being folded into the notice as a state.
          setKeyChecks(({ [targetId]: _dropped, ...rest }) => rest)
          setKeyCheckErrors((prev) => ({ ...prev, [targetId]: error.message }))
        })
    },
    [checkApiKey],
  )

  const sitesById = useMemo(
    () => new Map((config?.sites ?? []).map((site) => [site.id, site])),
    [config],
  )

  /**
   * Why a target cannot use detailed mode, or null if it can.
   *
   * Mirrors the backend's own rule (Monitor._default_mode) so the toggle promises only
   * what a probe can deliver: the site must have a key or a way to issue one, and this
   * host must be one the site's suffixes cover.
   */
  const detailedDisabledReason = useCallback(
    (result: TargetResult): string | null => {
      // The backend is authoritative: `supports_detailed` already accounts for a manual
      // host's own lifecycle (a synthetic site the frontend never sees). Trust it, and
      // only fall back to the site-based explanation to word *why* it is unavailable.
      if (result.supports_detailed) return null

      const site = sitesById.get(result.site_id)
      if (!site) {
        return 'Detailed mode is unavailable: no API key or issue endpoint for this host.'
      }
      const covered = site.trusted_host_suffixes.some(
        (suffix) =>
          result.host.split(':')[0] === suffix ||
          result.host.split(':')[0].endsWith('.' + suffix),
      )
      if (!covered) {
        return `${result.host} is not covered by ${site.name}'s trusted host suffixes, so a key would never be sent to it.`
      }
      if (!site.has_api_key && !site.can_issue_api_key) {
        return `No API key is configured for ${site.name} and it has no issue endpoint, so detailed mode can't get a key.`
      }
      return null
    },
    [sitesById],
  )

  /**
   * The filter chips: one per configured site, plus one per manual host.
   *
   * Configured chips come from config.sites and match by site_id. Manual hosts each get
   * their own chip (matching by target_id) so they are filterable in their own right -
   * a manual host's site_id is either a default site it was filed under or a synthetic
   * per-host id, neither of which gives it a visible chip otherwise.
   */
  const chips = useMemo<FilterChip[]>(() => {
    const configuredIds = new Set((config?.sites ?? []).map((s) => s.id))
    const configured: FilterChip[] = (config?.sites ?? []).map((site) => ({
      id: `site:${site.id}`,
      label: site.name,
      group: 'configured',
      matches: (t) => t.site_id === site.id,
    }))
    const manual: FilterChip[] = results
      .filter((r) => r.manual)
      .map((r) => ({
        id: `target:${r.target_id}`,
        label: `${r.host}${r.env_label && r.env_label !== 'Manual' ? ` · ${r.env_label}` : ''}`,
        group: 'manual',
        matches: (t) => t.target_id === r.target_id,
      }))
    // A manual host filed under a *configured* site would also be caught by that site's
    // chip; that is fine and expected - its own chip narrows to just it.
    void configuredIds
    return [...configured, ...manual]
  }, [config?.sites, results])

  /**
   * The estate the page is about: the site chips applied, the health filter not.
   *
   * The two filters are different kinds of thing, which is why they are applied at
   * different depths. Choosing sites changes *what you are looking after* - the overview
   * should shrink to it. Choosing a health slice changes *what you are looking for*
   * right now, and an overview that shrank to it could only ever say "1 of 1", which is
   * true of every filter and so tells you nothing.
   */
  const scoped = useMemo(() => {
    const filtered = results.filter((result) =>
      matchesChips(result, filters.siteIds, chips),
    )
    // Worst first. The alternative - a stable alphabetical order - reads tidier and
    // buries the one card an operator opened the page for.
    return [...filtered].sort(
      (a, b) =>
        STATUS_ORDER.indexOf(b.status) - STATUS_ORDER.indexOf(a.status) ||
        a.site_name.localeCompare(b.site_name) ||
        a.env_label.localeCompare(b.env_label),
    )
  }, [results, filters.siteIds, chips])

  /** What the grid, the table and the attention panel actually show. */
  const visible = useMemo(
    () => scoped.filter((result) => matchesHealth(result, filters.health)),
    [scoped, filters.health],
  )

  /** `visible` in grid order: a site's cards together, worst site first. */
  const cardOrder = useMemo(() => groupBySite(visible), [visible])

  // The hand-arranged order, if there is one, applied over that default. Keyed by
  // target id, so it survives every snapshot poll and a page reload alike.
  const cardIds = useMemo(() => cardOrder.map((r) => r.target_id), [cardOrder])
  const arranged = useCardOrder(cardIds)
  const byId = useMemo(
    () => new Map(visible.map((result) => [result.target_id, result])),
    [visible],
  )

  /** The roll-up for the health slice - what the attention panel is listing. */
  const rollup = useMemo(() => localRollup(visible), [visible])
  /** ...and for the estate in scope, which is what the overview summarises. */
  const scopedRollup = useMemo(() => localRollup(scoped), [scoped])

  const fatal = configError ?? snapshotError

  return (
    <div className="min-h-screen px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-[110rem] flex-col gap-4">
        <Header
          lastPollAt={snapshot?.last_poll_at ?? null}
          pollIntervalSeconds={pollIntervalSeconds}
          autoRefresh={autoRefresh}
          onAutoRefreshChange={setAutoRefresh}
          onRefresh={() => probe.mutate({ all_configured: true })}
          isRefreshing={probe.isPending}
          theme={theme.resolved}
          onToggleTheme={theme.toggle}
          staleWarning={
            fatal ? `Cannot reach the dashboard API: ${fatal.message}` : null
          }
          onSetGlobalInterval={(seconds) => setGlobalInterval.mutate(seconds)}
          globalIntervalPending={setGlobalInterval.isPending}
          onLogout={auth.authRequired ? () => logout.mutate() : undefined}
          logoutPending={logout.isPending}
        />

        {isLoading && !snapshot ? (
          <p className="card p-8 text-center text-sm text-ink-secondary">
            Polling every configured host for the first time…
          </p>
        ) : null}

        {fatal && !snapshot ? (
          <p className="card p-8 text-center text-sm text-critical">
            {fatal.message}
            <span className="mt-1 block text-xs text-ink-secondary">
              Is the backend running? <code>uvicorn app.main:app</code> in{' '}
              <code>backend/</code>.
            </span>
          </p>
        ) : null}

        {snapshot ? (
          <div className={cn('flex flex-col gap-4', isRefetching && 'refetching')}>
            <FilterBar
              chips={chips}
              filters={filters}
              onChange={setFilters}
              visibleCount={visible.length}
              totalCount={results.length}
              customOrder={arranged.isCustom}
              onResetOrder={arranged.reset}
            />

            <Overview
              snapshot={{ ...snapshot, results: scoped, rollup: scopedRollup }}
              health={filters.health}
            />

            <AttentionPanel rollup={rollup} results={visible} />

            {visible.length === 0 ? (
              <p className="card p-8 text-center text-sm text-ink-secondary">
                No targets match the current filters.
                {filters.health !== 'all' ? ' Nothing is in that state right now.' : ''}
              </p>
            ) : filters.view === 'table' ? (
              <TableView results={visible} history={snapshot.history} />
            ) : (
              // `items-start` so each card sizes to its own content. Without it a
              // short card is stretched to match the tallest in its row, which on a
              // target with no services leaves a large void under it.
              <SortableGrid
                ids={arranged.order}
                onMoveOnto={arranged.moveOnto}
                onMoveBy={arranged.moveBy}
                label={(id) => {
                  const result = byId.get(id)
                  return result ? `${result.site_name} ${result.env_label}` : id
                }}
                className="grid items-start gap-4 xl:grid-cols-2"
              >
                {(id, dragHandle) => {
                  const result = byId.get(id)
                  if (!result) return null
                  return (
                  <TargetCard
                    key={result.target_id}
                    result={result}
                    dragHandle={dragHandle}
                    history={snapshot.history[result.target_id]}
                    samples={applyWindow(
                      snapshot.history[result.target_id]?.samples ?? [],
                      filters.window,
                    )}
                    thresholds={thresholds}
                    // Its site carries the per-site and per-check latency bands.
                    site={sitesById.get(result.site_id)}
                    onProbe={(target: TargetResult) =>
                      probe.mutate({ target_ids: [target.target_id] })
                    }
                    isProbing={probe.isPending}
                    // Configured targets are defined in sites.json, so the backend
                    // refuses to delete them - no button rather than a 409.
                    onDelete={
                      result.manual
                        ? (target: TargetResult) => deleteTarget.mutate(target.target_id)
                        : undefined
                    }
                    isDeleting={
                      deleteTarget.isPending &&
                      deleteTarget.variables === result.target_id
                    }
                    onToggleEdit={
                      result.manual
                        ? () =>
                            setEditingId((id) =>
                              id === result.target_id ? null : result.target_id,
                            )
                        : undefined
                    }
                    isEditing={editingId === result.target_id}
                    editSlot={
                      editingId === result.target_id ? (
                        <EditManualForm
                          targetId={result.target_id}
                          isPending={updateManual.isPending}
                          error={updateManual.error?.message ?? null}
                          onClose={() => setEditingId(null)}
                          onSubmit={async (body) => {
                            await updateManual.mutateAsync({
                              targetId: result.target_id,
                              body,
                            })
                            setEditingId(null)
                          }}
                        />
                      ) : undefined
                    }
                    detailHref={targetHref(result.target_id)}
                    onOpenDetail={(target: TargetResult) => onOpenTarget(target.target_id)}
                    controls={{
                      detailedDisabledReason: detailedDisabledReason(result),
                      onSetMode: (mode) =>
                        setMode
                          .mutateAsync({ targetId: result.target_id, mode })
                          .then(rememberSettings),
                      modePending:
                        setMode.isPending &&
                        setMode.variables?.targetId === result.target_id,
                      intervalSeconds:
                        targetSettings[result.target_id]?.poll_interval_seconds ??
                        pollIntervalSeconds,
                      intervalIsCustom:
                        targetSettings[result.target_id]?.custom_interval ?? false,
                      onSetInterval: (seconds) =>
                        setTargetInterval
                          .mutateAsync({ targetId: result.target_id, seconds })
                          .then(rememberSettings),
                      intervalPending:
                        setTargetInterval.isPending &&
                        setTargetInterval.variables?.targetId === result.target_id,
                      onCheckKey: () => onCheckKey(result.target_id),
                      checkKeyPending:
                        checkApiKey.isPending &&
                        checkApiKey.variables === result.target_id,
                      keyCheck: keyChecks[result.target_id] ?? null,
                      keyCheckError: keyCheckErrors[result.target_id] ?? null,
                    }}
                  />
                  )
                }}
              </SortableGrid>
            )}

            <ManualTargetForm
              sites={config?.sites ?? []}
              onSubmit={(target: ManualTargetRequest) =>
                probe.mutate({ manual: [target] })
              }
              isPending={probe.isPending}
              rejected={probe.data?.rejected ?? []}
              // From the full result set, not the filtered one: a monitor hidden by a
              // site filter must still be removable.
              monitors={results.filter((result) => result.manual)}
              onRemove={(targetId: string) => deleteTarget.mutate(targetId)}
              removingId={
                deleteTarget.isPending ? (deleteTarget.variables ?? null) : null
              }
              removeError={deleteTarget.error?.message ?? null}
            />

            <AddCategoryForm
              onSubmit={(body: NewCategoryRequest) => addCategory.mutateAsync(body)}
              isPending={addCategory.isPending}
              error={addCategory.error?.message ?? null}
              existingIds={(config?.sites ?? []).map((site) => site.id)}
            />

            <footer className="pb-2 text-center text-[11px] text-ink-muted">
              Polled server-side; API keys never reach the browser.
              {snapshot.poll_count > 0
                ? ` ${snapshot.poll_count} poll cycle${snapshot.poll_count === 1 ? '' : 's'} since start.`
                : ''}
            </footer>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The grid's default order: every card of one site together, worst site first.
 *
 * A site's environments are read as a set - "how is WhiteHawk doing" is one question,
 * answered by looking at production and staging next to each other. Ordering the grid by
 * status alone scattered them down the page, so answering that question meant scrolling
 * past three unrelated cards and holding the first one in your head.
 *
 * So the *group* is what status orders: the site with the worst card in it leads, which
 * keeps the thing most likely to be the incident at the top of the page, and its
 * siblings come with it. Within a group the same rule applies again - worst card first -
 * and then shortest first, which is what decides who shares a row.
 *
 * That last tiebreak is the one non-obvious part. The grid is two columns of
 * `items-start`, so a row is as tall as its taller card and the shorter one leaves a
 * void beneath it. Pairing a one-line public card with a detailed one carrying six
 * checks and a queue panel is what opens those voids, so cards of a similar height are
 * put beside each other and the tall ones sink to the end of their group.
 *
 * Nothing here can bury a problem: AttentionPanel lists everything wrong above the grid
 * regardless of how the cards below it are arranged.
 */
function groupBySite(results: TargetResult[]): TargetResult[] {
  const groups = new Map<string, TargetResult[]>()
  for (const result of results) {
    const group = groups.get(result.site_id)
    if (group) group.push(result)
    else groups.set(result.site_id, [result])
  }

  const severity = (group: TargetResult[]) =>
    Math.max(...group.map((r) => STATUS_ORDER.indexOf(r.status)))

  return [...groups.values()]
    .sort(
      (a, b) =>
        severity(b) - severity(a) || a[0].site_name.localeCompare(b[0].site_name),
    )
    .flatMap((group) =>
      [...group].sort(
        (a, b) =>
          STATUS_ORDER.indexOf(b.status) - STATUS_ORDER.indexOf(a.status) ||
          cardBulk(a) - cardBulk(b) ||
          a.env_label.localeCompare(b.env_label),
      ),
    )
}

/**
 * What a queue panel costs, expressed in service rows.
 *
 * The pipeline bar, the consumer counts and the collapsed per-family toggle come to
 * roughly six rows' worth of card - enough that a card carrying one is in a different
 * height class from a card that is only a list of checks.
 */
const QUEUE_PANEL_ROWS = 6

/**
 * Roughly how tall a card will render, in service rows.
 *
 * Not a measurement - it only has to *order* cards, and the two things that decide a
 * card's height are how many checks it lists and whether it carries a queue panel. The
 * queue test mirrors TargetCard's own, so this predicts what that card will actually
 * draw rather than guessing from `queue_totals`.
 *
 * Deliberately a function of the snapshot alone. Folding in card-local state - an
 * expanded raw payload, an open edit form - would re-sort the grid under the hands of
 * whoever just opened one, which is a worse problem than the gap this closes.
 */
function cardBulk(result: TargetResult): number {
  const hasQueuePanel = result.services.some(
    (service) => service.data?.families && service.data?.totals,
  )
  return result.services.length + (hasQueuePanel ? QUEUE_PANEL_ROWS : 0)
}

/**
 * Recompute the roll-up over a subset.
 *
 * Mirrors `Monitor.rollup` on the backend: worst-status wins, and queue totals are
 * summed only across targets that actually returned them, so `queue_targets === 0`
 * stays distinguishable from a genuine zero.
 */
function localRollup(results: TargetResult[]): Rollup {
  const byStatus: Rollup['by_status'] = {}
  for (const result of results) {
    byStatus[result.status] = (byStatus[result.status] ?? 0) + 1
  }

  const degraded: Rollup['degraded_services'] = []
  let servicesTotal = 0
  for (const result of results) {
    for (const service of result.services) {
      servicesTotal += 1
      if (service.ok === false) {
        degraded.push({
          target_id: result.target_id,
          site_name: result.site_name,
          env_label: result.env_label,
          service: service.name,
          important: service.important,
          detail: service.detail,
        })
      }
    }
  }

  const queueKeys = [
    'queued',
    'retrying',
    'cooling_off',
    'parked',
    'consumers_attached',
    'consumers_active',
    'consumers_standby',
  ] as const
  const queueTotals: Record<string, number> = {}
  for (const key of queueKeys) queueTotals[key] = 0

  let queueTargets = 0
  for (const result of results) {
    if (!result.queue_totals) continue
    queueTargets += 1
    for (const key of queueKeys) {
      const value = result.queue_totals[key]
      if (typeof value === 'number') queueTotals[key] += value
    }
  }

  const latencies = results
    .map((result) => result.elapsed_ms)
    .filter((value): value is number => typeof value === 'number')

  return {
    overall: results.length > 0 ? worstStatus(results.map((r) => r.status)) : 'healthy',
    target_count: results.length,
    by_status: byStatus,
    healthy_count: byStatus.healthy ?? 0,
    services_total: servicesTotal,
    services_down: degraded.length,
    degraded_services: degraded,
    queue_totals: queueTargets > 0 ? queueTotals : null,
    queue_targets: queueTargets,
    slowest_ms: latencies.length > 0 ? Math.max(...latencies) : null,
    authenticated_targets: results.filter((result) => result.view === 'detailed').length,
  }
}
